import { useState } from "react";
import { AGE_RANGES, COMPLETING_FOR } from "../../lib/options";
import { buildPayload, useSubmit, validateContact, validateLocation } from "../../lib/forms";
import { Wizard } from "./Wizard";
import { ChoiceCards, ConsentBlock, ConsentRow, ContactFields, Group, Honeypot, LocationFields, MultiChips, SuccessPanel, TextArea, TextField } from "./FormBits";
import { Notice } from "../site/Blocks";

const COMFORT = ["Yes, I am comfortable with this", "I would prefer not to, but I am open to it", "No, I am not comfortable with this"];
const TIMEFRAME = ["Within 30 days", "In 1–3 months", "In 3–6 months", "More than 6 months", "Just exploring options"];
const FULLY = "Yes, fully independently";
const INDEPENDENCE = [FULLY, "Yes, with some occasional support", "Needs regular daily support", "Not sure", "Other"];
const SUPPORT_NEEDS = ["Meal preparation", "Medication reminders", "Personal care", "Housekeeping", "Companionship", "Help with appointments and errands", "Daily check-ins"];
const ACCESS = ["Wheelchair accessible", "Step-free entry", "Grab bars or accessible bathroom", "Ground-floor room", "Service animal", "No specific needs"];
const GOV_RENT = "Government income support";
const SUBSIDY = "Housing subsidy or program";
const RENT = ["Self-pay from income or savings", "Family contribution", GOV_RENT, SUBSIDY, "Not sure yet"];

const INITIAL = {
  inquiry_type: "independent", completing_for: "", relationship: "", organization: "", age_range: "", gender: "", gender_other: "", region: "", city: "", city_custom: "",
  shared_spaces_ok: "", shared_bedroom_ok: "", single_occupancy_ack: false, move_in_timeframe: "", independence: "",
  support_needs: [], support_details: "", accessibility: [], accessibility_other: "", rent_payment: "", rent_program_details: "", additional_notes: "",
  name: "", email: "", phone: "", preferred_contact: "", privacy_consent: false, marketing_consent: false, website: "",
};

const clean = (f) => {
  const c = { ...f };
  if (f.completing_for !== "professional") c.organization = "";
  if (!["family", "friend"].includes(f.completing_for)) c.relationship = "";
  if (f.gender !== "Other") c.gender_other = "";
  if (f.independence === FULLY) Object.assign(c, { support_needs: [], support_details: "" });
  if (![GOV_RENT, SUBSIDY].includes(f.rent_payment)) c.rent_program_details = "";
  return c;
};

export const IndependentLivingQuestionnaire = () => {
  const [form, setForm] = useState(INITIAL);
  const { submit, submitting, error, reference, reset } = useSubmit();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const needsSupport = form.independence && form.independence !== FULLY;
  const detailsRequired = needsSupport && form.independence !== "Not sure";

  if (reference) {
    return (
      <SuccessPanel reference={reference} title="Thank you. Your independent living inquiry has been received." testId="housing-success"
        onReset={() => { setForm(INITIAL); reset(); }}>
        <p>Our team will review your inquiry and contact you to discuss next steps.</p>
        <p>Submitting an inquiry does not reserve a bed or guarantee a placement.</p>
      </SuccessPanel>
    );
  }

  const steps = [
    {
      title: "About this inquiry", short: "About",
      validate: () => (!form.completing_for ? "Please tell us who this inquiry is for." : !form.age_range ? "Please select an age range."
        : !form.gender ? "Please select a gender." : form.gender === "Other" && !form.gender_other.trim() ? "Please specify gender." : null),
      content: (
        <>
          <Group legend="Who is this inquiry for?" required>
            <ChoiceCards options={COMPLETING_FOR} value={form.completing_for} onChange={(v) => set("completing_for", v)} testId="housing-completing-for" />
          </Group>
          {form.completing_for === "professional" && (
            <TextField id="housing-org" label="Organization (optional)" value={form.organization} onChange={(v) => set("organization", v)} testId="housing-input-organization" />
          )}
          {["family", "friend"].includes(form.completing_for) && (
            <TextField id="housing-rel" label="Your relationship to the person" value={form.relationship} onChange={(v) => set("relationship", v)} testId="housing-input-relationship" />
          )}
          <Group legend="Age range of the person seeking housing" required>
            <ChoiceCards options={AGE_RANGES} value={form.age_range} onChange={(v) => set("age_range", v)} cols="grid-cols-2 sm:grid-cols-5" testId="housing-age" />
          </Group>
          <Group legend="Gender of the person seeking housing" required>
            <ChoiceCards options={["Female", "Male", "Other"]} value={form.gender} onChange={(v) => set("gender", v)} cols="sm:grid-cols-3" testId="housing-gender" />
          </Group>
          {form.gender === "Other" && (
            <TextField id="housing-gender-other" label="Please specify" value={form.gender_other} onChange={(v) => set("gender_other", v)} required maxLength={120} testId="housing-input-gender-other" />
          )}
        </>
      ),
    },
    {
      title: "Current location", short: "Location", validate: () => validateLocation(form),
      content: <LocationFields form={form} set={set} prefix="housing" legend="Where does the person currently live?" />,
    },
    {
      title: "Shared living and move-in", short: "Shared living",
      validate: () => (!form.shared_spaces_ok ? "Please tell us if you are comfortable sharing living spaces."
        : !form.shared_bedroom_ok ? "Please tell us if you are comfortable sharing a bedroom."
        : !form.single_occupancy_ack ? "Please confirm that you understand this housing is for one single adult."
        : !form.move_in_timeframe ? "Please select an intended move-in timeframe." : null),
      content: (
        <>
          <Notice>Our independent living home is shared and is for single adults only. It is not suitable for couples or for anyone bringing children to live with them. Residents share living spaces and bedrooms, and each resident has their own bed and nightstand.</Notice>
          <Group legend="Are you comfortable sharing living spaces such as the kitchen, living room and bathroom?" required>
            <ChoiceCards options={COMFORT} value={form.shared_spaces_ok} onChange={(v) => set("shared_spaces_ok", v)} cols="sm:grid-cols-1" testId="housing-shared-spaces" />
          </Group>
          <Group legend="Are you comfortable sharing a bedroom?" required>
            <ChoiceCards options={COMFORT} value={form.shared_bedroom_ok} onChange={(v) => set("shared_bedroom_ok", v)} cols="sm:grid-cols-1" testId="housing-shared-bedroom" />
          </Group>
          <div className="rounded-2xl border border-[#E5E0D8] bg-[#F3EFEA]/60 p-5">
            <ConsentRow id="housing-single-ack" checked={form.single_occupancy_ack} onChange={(v) => set("single_occupancy_ack", v)} testId="housing-input-single-occupancy">
              I understand this accommodation is for me only, as a single adult. Couples, children and other household members cannot live with me. Guests can be discussed during intake.<span className="text-[#B25B41]"> *</span>
            </ConsentRow>
          </div>
          <Group legend="Intended move-in timeframe" required>
            <ChoiceCards options={TIMEFRAME} value={form.move_in_timeframe} onChange={(v) => set("move_in_timeframe", v)} cols="sm:grid-cols-2 lg:grid-cols-3" testId="housing-timeframe" />
          </Group>
        </>
      ),
    },
    {
      title: "Independence and support needs", short: "Needs",
      validate: () => (!form.independence ? "Please tell us about the ability to live independently."
        : detailsRequired && form.support_details.trim().length < 3 ? "Please describe the supports needed." : null),
      content: (
        <>
          <Group legend="Is the person able to live independently?" required>
            <ChoiceCards options={INDEPENDENCE} value={form.independence} onChange={(v) => set("independence", v)} testId="housing-independence" />
          </Group>
          {needsSupport && (
            <div className="space-y-6 animate-step" data-testid="housing-support-details-block">
              <TextArea id="housing-support-details" label={form.independence === "Other" ? "Please describe" : "What supports are needed?"} value={form.support_details}
                onChange={(v) => set("support_details", v)} required={detailsRequired} max={1000} rows={3}
                hint="For example, help with meals twice a week or reminders to take medication. Please avoid detailed medical history." testId="housing-input-support-details" />
              <Group legend="Which of these would help? (optional)">
                <MultiChips options={SUPPORT_NEEDS} value={form.support_needs} onChange={(v) => set("support_needs", v)} testId="housing-support" />
              </Group>
            </div>
          )}
          <Group legend="Accessibility needs (optional)">
            <MultiChips options={ACCESS} value={form.accessibility} onChange={(v) => set("accessibility", v)} testId="housing-access" />
          </Group>
          <TextField id="housing-access-other" label="Other accessibility details (optional)" value={form.accessibility_other} onChange={(v) => set("accessibility_other", v)} maxLength={400} testId="housing-input-access-other" />
        </>
      ),
    },
    {
      title: "Rent arrangements", short: "Rent",
      validate: () => (!form.rent_payment ? "Please select an anticipated rent-payment arrangement."
        : [GOV_RENT, SUBSIDY].includes(form.rent_payment) && !form.rent_program_details.trim() ? "Please tell us which program or subsidy." : null),
      content: (
        <>
          <Group legend="How do you anticipate rent being paid?" required hint="We only need a general idea. Please do not share income amounts or banking details.">
            <ChoiceCards options={RENT} value={form.rent_payment} onChange={(v) => set("rent_payment", v)} testId="housing-rent" />
          </Group>
          {form.rent_payment === GOV_RENT && (
            <TextField id="housing-rent-program" label="Which program do you receive?" value={form.rent_program_details} onChange={(v) => set("rent_program_details", v)} required maxLength={400}
              placeholder="e.g. ODSP, Ontario Works, COHB" testId="housing-input-income-program" />
          )}
          {form.rent_payment === SUBSIDY && (
            <TextField id="housing-rent-subsidy" label="Which housing subsidy or program?" value={form.rent_program_details} onChange={(v) => set("rent_program_details", v)} required maxLength={400}
              placeholder="e.g. rent-geared-to-income, portable housing benefit" testId="housing-input-subsidy-program" />
          )}
          <TextArea id="housing-notes" label="Anything else you would like us to know? (optional)" value={form.additional_notes} onChange={(v) => set("additional_notes", v)}
            hint="Please avoid detailed medical or financial information." max={1000} testId="housing-input-notes" />
        </>
      ),
    },
    {
      title: "Your contact details", short: "Contact", validate: () => validateContact(form),
      content: (
        <>
          <ContactFields form={form} set={set} prefix="housing" />
          <ConsentBlock form={form} set={set} prefix="housing" purpose="housing inquiry" />
          <Honeypot value={form.website} onChange={(v) => set("website", v)} />
        </>
      ),
    },
  ];

  return (
    <Wizard steps={steps} testPrefix="housing" submitting={submitting} error={error} submitLabel="Submit independent living inquiry"
      onSubmit={() => submit("/public/housing-inquiry", buildPayload(clean(form)))} />
  );
};
